/**
 * Phase 6b — Executive Digest Center
 * Permission: EXEC_DIGEST_CONFIG (super_admin, direktur_utama, direktur_operasional).
 * Semua user bisa lihat notifications sendiri; super_admin lihat semua subscription.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Mailbox, PlayCircle, BellRing, Send, RotateCw } from "lucide-react";

const STATUS_BADGE = {
  READY:         { cls: "bg-sky-50 text-sky-900 border-sky-200",         label: "Menunggu pengiriman" },
  SENDING:       { cls: "bg-amber-50 text-amber-900 border-amber-200",   label: "Mengirim" },
  SENT:          { cls: "bg-emerald-50 text-emerald-900 border-emerald-200", label: "Terkirim" },
  FAILED:        { cls: "bg-rose-50 text-rose-900 border-rose-200",      label: "Gagal" },
  RETRY_PENDING: { cls: "bg-amber-100 text-amber-900 border-amber-200",  label: "Retry Menunggu" },
};

export default function ExecutiveDigest() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [queue, setQueue] = useState([]);
  const [subsAll, setSubsAll] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [emailProvider, setEmailProvider] = useState(false);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  const isSuper = user?.role === "super_admin";

  const loadAll = () => {
    setErr(null);
    api.get("/executive/digest/subscription")
      .then(({ data }) => setSub(data))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengelola subscription digest.");
        else setErr(formatApiError(e));
      });
    api.get("/executive/digest/queue")
      .then(({ data }) => {
        setQueue(data.items || []);
        setEmailProvider(!!data.has_email_provider);
      })
      .catch(() => {});
    api.get("/executive/notifications")
      .then(({ data }) => setNotifs(data.items || []))
      .catch(() => {});
    if (isSuper) {
      api.get("/executive/digest/subscriptions")
        .then(({ data }) => setSubsAll(data.items || []))
        .catch(() => {});
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [isSuper]);

  const saveSub = async (patch) => {
    try {
      const body = {
        enabled: patch.enabled !== undefined ? patch.enabled : sub.enabled,
        frequency: "MONTHLY",
        delivery_channel: patch.delivery_channel || sub.delivery_channel || "INAPP",
        timezone: sub.timezone || "Asia/Jakarta",
      };
      const { data } = await api.put("/executive/digest/subscription", body);
      setSub(data);
      toast.success("Subscription disimpan.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/executive/digest/run-monthly");
      toast.success(`Digest ${data.period} — Diproses: ${data.processed}, Terdedup: ${data.deduped}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRunning(false); }
  };

  const ackNotif = async (id) => {
    try {
      await api.post(`/executive/notifications/${id}/acknowledge`);
      setNotifs((prev) => prev.filter((n) => n.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendOne = async (qid) => {
    try {
      const { data } = await api.post(`/executive/digest/${qid}/send`);
      toast.success(`Pengiriman: ${data.status}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const retryOne = async (qid) => {
    try {
      const { data } = await api.post(`/executive/digest/${qid}/retry`);
      toast.success(`Retry: ${data.status}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-digest-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-digest-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exec-digest-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Mailbox className="h-6 w-6 text-sky-700" /> Digest Eksekutif
          </h1>
          <p className="text-xs md:text-sm text-slate-500">
            Executive Monthly Digest otomatis H+1 akhir bulan (07:00 WIB) via platform scheduler.
            Konten di-filter per peran (RBAC) — payroll/HR mengikuti EXEC_HR_FULL.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={runNow} disabled={running || !sub?.enabled}
                 data-testid="digest-run-now">
          <PlayCircle size={14} className="mr-1" />Jalankan Manual
        </Button>
      </div>

      {!emailProvider && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900"
             data-testid="digest-no-email-banner">
          Belum ada integrasi email provider. Digest akan dibangkitkan ke arsip + Notification Center dengan status
          <span className="font-semibold"> READY (Digest menunggu pengiriman)</span>. Ini bukan kegagalan.
        </div>
      )}

      {/* Subscription Card */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Subscription Saya</CardTitle></CardHeader>
        <CardContent>
          {!sub ? (
            <div className="text-xs text-slate-400 italic">Memuat…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Status</div>
                <div className="flex items-center gap-2">
                  <Switch checked={!!sub.enabled} onCheckedChange={(v) => saveSub({ enabled: v })}
                           data-testid="digest-sub-enabled" />
                  <span className="text-xs">{sub.enabled ? "Aktif" : "Nonaktif"}</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Frekuensi</div>
                <div className="text-sm font-semibold">MONTHLY (H+1)</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Delivery</div>
                <div className="text-sm font-semibold">{sub.delivery_channel || "INAPP"}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Terakhir Terkirim</div>
                <div className="text-sm">
                  {sub.last_sent_at ? new Date(sub.last_sent_at).toLocaleString("id-ID") : "—"}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Queue */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Antrian Digest</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Periode</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Peran</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempt</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Terkirim</TableHead>
                <TableHead>Error Terakhir</TableHead>
                <TableHead>Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada digest untuk periode ini
                </TableCell></TableRow>
              )}
              {queue.map((q) => {
                const sb = STATUS_BADGE[q.status] || STATUS_BADGE.READY;
                return (
                <TableRow key={q.id} data-testid={`q-row-${q.id}`}>
                  <TableCell className="font-mono text-xs">{q.period}</TableCell>
                  <TableCell className="text-xs">{q.user_id}</TableCell>
                  <TableCell className="text-xs">{q.role || "—"}</TableCell>
                  <TableCell className="text-xs">{q.delivery_channel}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={sb.cls} data-testid={`q-status-${q.id}`}>
                      {sb.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-xs">{q.attempt_count ?? 0}/4</TableCell>
                  <TableCell className="text-xs">{q.provider || "—"}</TableCell>
                  <TableCell className="text-xs">{q.sent_at ? new Date(q.sent_at).toLocaleString("id-ID") : "—"}</TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={q.last_error_message || ""}>
                    {q.last_error_code ? <span className="text-rose-700">{q.last_error_code}</span> : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {q.status === "READY" && (
                        <Button variant="outline" size="sm" onClick={() => sendOne(q.id)} data-testid={`q-send-${q.id}`}>
                          <Send size={11} className="mr-1" />Kirim
                        </Button>
                      )}
                      {(q.status === "FAILED" || q.status === "RETRY_PENDING") && (
                        <Button variant="outline" size="sm" onClick={() => retryOne(q.id)} data-testid={`q-retry-${q.id}`}>
                          <RotateCw size={11} className="mr-1" />Kirim Ulang
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Notification Center (own) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BellRing className="h-4 w-4 text-sky-700" /> Notifikasi Eksekutif Saya
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prioritas</TableHead>
                <TableHead>Tipe</TableHead>
                <TableHead>Judul</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead>Pesan</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notifs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada notifikasi
                </TableCell></TableRow>
              )}
              {notifs.map((n) => (
                <TableRow key={n.id} data-testid={`notif-row-${n.id}`}>
                  <TableCell>
                    <Badge variant="outline" className={
                      n.priority === "CRITICAL" ? "bg-rose-100 text-rose-900 border-rose-200" :
                      n.priority === "WARNING" ? "bg-amber-100 text-amber-900 border-amber-200" :
                      "bg-sky-100 text-sky-900 border-sky-200"}>
                      {n.priority}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-[11px]">{n.event_type}</TableCell>
                  <TableCell className="text-xs font-semibold">{n.title}</TableCell>
                  <TableCell className="text-xs">{n.period || "—"}</TableCell>
                  <TableCell className="text-xs max-w-md">{n.message}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {n.drill_to && (
                        <Link to={n.drill_to} className="text-xs text-sky-700 hover:underline">
                          Buka
                        </Link>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => ackNotif(n.id)}
                               data-testid={`notif-ack-${n.id}`}>
                        Tandai
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Super admin: all subscriptions */}
      {isSuper && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Subscription Pengguna (Super Admin)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Peran</TableHead>
                  <TableHead>Aktif</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Terakhir Terkirim</TableHead>
                  <TableHead>Diperbarui</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subsAll.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-400 italic py-6">
                    Belum ada user yang subscribe
                  </TableCell></TableRow>
                )}
                {subsAll.map((s) => (
                  <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                    <TableCell className="text-xs">{s.user_id}</TableCell>
                    <TableCell className="text-xs">{s.role || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={s.enabled ? "bg-emerald-50 text-emerald-900 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}>
                        {s.enabled ? "Aktif" : "Nonaktif"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{s.delivery_channel}</TableCell>
                    <TableCell className="text-xs">{s.last_sent_at ? new Date(s.last_sent_at).toLocaleString("id-ID") : "—"}</TableCell>
                    <TableCell className="text-xs">{new Date(s.updated_at).toLocaleString("id-ID")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
